import React, { useState, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import PublishStatusComponent from '../../components/PublishStatusComponent';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { SportsManagement } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import { Sport } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Sports/PathItems';
import { Published } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Core/PathItems';

interface EditSportProps {
  id: string;
  onBack: () => void;
}

function EditSport({ id, onBack }: EditSportProps) {
  const { onError } = useToast();
  const [sport, setSport] = useState<Sport | null>(null);
  const [name, setName] = useState('');
  const [translationKey, setTranslationKey] = useState('');
  const [state, setState] = useState<Published>({ state: 'draft', isDirty: false });

  const loadSport = async () => {
    onError((await Session.getClient().addPathParam('sportId', id).request(SportsManagement.getSport()))
      .maybeOk((s: Sport) => {
        if (s) {
          setSport(s);
          setName(s.name);
          setTranslationKey(s.translationKey);
          setState(s.state);
        }
      }));
  };

  useEffect(() => {
    loadSport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function patchField<T>(
    operation: any,
    newValue: T,
    currentValue: T,
    revertHandler: (value: T) => void
  ): Promise<void> {
    if (newValue === currentValue) return;
    const client = Session.getClient().addPathParam('sportId', id);
    onError((await client.request(operation)).maybeOk(loadSport));
  }

  if (!sport) return null;

  return (
    <Box sx={{ p: 3 }}>
      <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mb: 2 }}>
        <ArrowBackIcon />
      </IconButton>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Sport Details
        </Typography>
        <PublishStatusComponent
          status={state}
          onTransition={async (action) => {
            return (await Session.getClient().addPathParam('sportId', id).request(SportsManagement.updateSportState({ stateTransition: action })))
              .maybeOk(() => loadSport());
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 350, maxWidth: 500 }}>
        <TextField
          label="Sport Name"
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={() => patchField(SportsManagement.updateSportName({ name }), name, sport.name, setName)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Translation Key"
          value={translationKey}
          disabled
          sx={{ mb: 2 }}
        />
      </Box>
    </Box>
  );
}

export default EditSport;
